import type { FormikHelpers } from 'formik';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAddRegistryItemPhotoMutation, CustomItemDataType, useCreateRegistryItemMutation } from '@graphql/generated';
import { useTranslation } from '@shared/core/i18n';
import type { EditGiftDailogFields } from './types';
import { DEFAULT_CURRENCY_SYMBOL } from '@shared/utils/currency/constants';
import { useFilestack } from '@shared/utils/filestack';
import { useCallback, useState } from 'react';
import { useTrackImpactRadius } from '@shared/utils/impactRadius/useTrackImpactRadius';
import { isValidUrlAndDomain } from '@shared/utils/urlHelpers';

interface EditDialogControllerProps {
  eventId: string;
  showAddressReminderDialog?: () => void;
  showCurrencyDialog: () => void;
  onClose: () => void;
}

export const useAddCustomGiftDialogController = ({ eventId, onClose, showAddressReminderDialog, showCurrencyDialog }: EditDialogControllerProps) => {
  const { t: translateForm } = useTranslation('generalFormValidation');
  const { t } = useTranslation('sharedRegistry');
  const { linkInvalid, quantityMinError } = t('giftDialog');

  const trackImpactRadius = useTrackImpactRadius();

  const [updatedPhoto, setUpdatedPhoto] = useState<string | undefined>(undefined);
  const [photoFilename, setPhotoFilename] = useState<string | undefined>(undefined);

  const [createRegistryItem, { loading: createRegistryItemLoading }] = useCreateRegistryItemMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries', 'GetRegistryOnboardingSuggestions', 'RegistryGiftBasketCountByEventName'],
    awaitRefetchQueries: true,
  });

  const [uploadPhoto] = useAddRegistryItemPhotoMutation({
    refetchQueries: () => ['GetAdminRegistryRegistries'],
  });
  const handleSubmit = async (editGiftDialogFields: EditGiftDailogFields, actions: FormikHelpers<EditGiftDailogFields>) => {
    const priceToFix = editGiftDialogFields.price ? +editGiftDialogFields.price : 0;

    const newRegistryItem = await createRegistryItem({
      variables: {
        eventId: eventId,
        payload: {
          totalRequested: editGiftDialogFields.qty,
          note: editGiftDialogFields.note,
          custom: {
            type: CustomItemDataType.gift,
            title: editGiftDialogFields.title || '',
            url: editGiftDialogFields.link || '',
            priceAsFloatingPointDecimalString: priceToFix.toFixed(2),
            imageUrlForImport: editGiftDialogFields.image || '',
          },
          metadata: {
            source: 'manual',
            sourceMetadata: {
              scannedPrice: priceToFix.toFixed(2),
            },
          },
        },
      },
    });
    const id = newRegistryItem.data?.createRegistryItem.id;
    if (photoFilename && id) {
      await uploadPhoto({ variables: { assetId: photoFilename, id } });
    }
    trackImpactRadius('Product_Added');
    actions.resetForm();
    clearPhoto();
    if (showAddressReminderDialog) {
      showAddressReminderDialog();
    }

    onClose();
  };
  const minQyt = 1;
  const quantityError = quantityMinError();

  const formik = useFormik<EditGiftDailogFields>({
    enableReinitialize: true,
    initialValues: {
      currency: DEFAULT_CURRENCY_SYMBOL,
      price: 0,
      qty: 1,
      note: '',
      title: '',
      link: '',
      image: '',
    },
    onSubmit: handleSubmit,
    validationSchema: Yup.object<EditGiftDailogFields>({
      note: Yup.string().max(512),
      title: Yup.string().required(translateForm('required')),
      link: Yup.string()
        .required(translateForm('required'))
        .test('is-valid-url-and-domain', linkInvalid(), value => Boolean(value) && isValidUrlAndDomain(value)),
      qty: Yup.number().min(minQyt, quantityError).integer().required(translateForm('required')).min(minQyt, quantityError),
      price: Yup.number().min(0).required(translateForm('required')),
    }),
  });

  const registryItemLoading = createRegistryItemLoading;

  const clearPhoto = () => {
    setUpdatedPhoto(undefined);
    setPhotoFilename(undefined);
  };

  const { open: openFilepicker, getPhotoPreviewUrl } = useFilestack({
    containerId: eventId || '',
    transformations: {
      crop: {
        aspectRatio: 1 / 1,
        force: true,
      },
    },
    onUploadDone: async res => {
      const { filename, url } = res.filesUploaded[0];
      const photo = await getPhotoPreviewUrl({
        url,
      });
      setUpdatedPhoto(photo);
      setPhotoFilename(filename);
    },
    maxFiles: 1,
  });

  const handleConfirm = () => {
    formik.setFieldValue('image', updatedPhoto);
    if (!formik.isSubmitting) {
      formik.submitForm();
    }
  };

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  const handleTooltipClick = useCallback(
    e => {
      e.preventDefault();
      onClose();
      showCurrencyDialog();
    },
    [onClose, showCurrencyDialog],
  );

  return {
    formik,
    registryItemLoading,
    showCurrencyDialog,
    updatedPhoto,
    openFilepicker,
    handleTooltipClick,
    handleCancel,
    handleConfirm,
  };
};
